import * as React from "react";
import { tv } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ChevronIcon } from "./ChevronIcon";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { Link } from "./Link";
import {
  ImageWithAspectRatioProps,
  LinkProps,
  CardBackgroundColorType,
  CardPaddingSizeType,
} from "./types";

export const cardAlignable = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:h-full uipm:font-normal uipm:justify-between",
    "uipm:overflow-hidden",
  ),
  variants: {
    roundedCorners: {
      always: "uipm:rounded-[var(--radius-lg)]",
      responsive: "uipm:rounded-none uipm:md:rounded-[var(--radius-lg)]",
    },
    backgroundColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "secondary-light": "uipm:bg-[var(--surface-surface-accent-light)]",
      light: "uipm:bg-[var(--surface-surface-secondary)]",
      "very-light": "uipm:bg-[#F7F7F7]",
      white: "uipm:bg-[var(--surface-surface-primary)]",
      none: "uipm:bg-transparent",
    },
    hasLink: {
      true: "uipm:gap-6 uipm:justify-between",
      false: "",
    },
    aligning: {
      left: "uipm:items-start uipm:text-left",
      center: "uipm:items-center uipm:text-center",
    },
    verticalPadding: {
      default: "uipm:py-[var(--container-card-padding)]",
      small: "uipm:py-[var(--container-card-padding)]",
      medium: "uipm:py-[var(--container-card-medium-padding)]",
      large: "uipm:py-[var(--container-card-large-padding)]",
    },
    horizontalPadding: {
      default: "uipm:px-[var(--container-card-padding)]",
      small: "uipm:px-[var(--container-card-padding)]",
      medium: "uipm:px-[var(--container-card-medium-padding)]",
      large: "uipm:px-[var(--container-card-large-padding)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
    hasLink: false,
    aligning: "left",
    verticalPadding: "default",
    horizontalPadding: "default",
    roundedCorners: "responsive",
  },
});

const iconContainer = tv({
  base: "uipm:relative uipm:flex-shrink-0 uipm:overflow-hidden",
  variants: {
    iconSize: {
      small: cn("uipm:w-11 uipm:h-11 uipm:mb-4", "uipm:md:w-16 uipm:md:h-16"),
      large: cn(
        "uipm:w-20 uipm:h-20 uipm:my-3",
        "uipm:md:w-24 uipm:md:h-24 uipm:md:my-4",
      ),
      image: cn("uipm:w-full uipm:mb-4", "uipm:rounded-lg uipm:relative"),
    },
    aligning: {
      left: "uipm:self-start",
      center: "uipm:self-center",
    },
  },
  defaultVariants: {
    iconSize: "small",
    aligning: "left",
  },
});

const textWrapper = tv({
  base: "uipm:font-normal uipm:break-words",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted-faded)]",
      secondary: "uipm:text-[var(--typography-colors-text-accent)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-accent)]",
      light: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "very-light": "uipm:text-[var(--typography-colors-text-faded-dark)]",
      white: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      none: "uipm:text-[var(--typography-colors-text-faded-dark)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const linkWrapper = tv({
  base: "uipm:font-semibold uipm:flex uipm:items-center uipm:gap-[var(--container-gutter)]",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted)]",
      secondary: "uipm:text-[var(--typography-colors-link)]",
      "secondary-light": "uipm:text-[var(--typography-colors-link)]",
      light: "uipm:text-[var(--typography-colors-link)]",
      "very-light": "uipm:text-[var(--typography-colors-link)]",
      white: "uipm:text-[var(--typography-colors-link)]",
      none: "uipm:text-[var(--typography-colors-link)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

export interface CardAlignableProps
  extends Omit<ComponentProps<"div">, "title"> {
  icon?: ImageWithAspectRatioProps | null;
  iconSize?: "small" | "large" | "image";
  title?: TitleProps | null;
  text?: SimpleRichTextProps | null;
  links?: LinkProps[] | null;
  backgroundColor?: CardBackgroundColorType;
  aligning?: "left" | "center";
  verticalPadding?: CardPaddingSizeType;
  horizontalPadding?: CardPaddingSizeType;
  roundedCorners?: "always" | "responsive";
  onCardClick?: (
    cardTitle?: string,
    contentDescription?: string,
    ctaText?: string,
  ) => void;
  onCardHover?: () => void;
  onLinkClick?: (linkText: string, linkIndex: number) => void;
  cardAspectRatio?: string;
}

export const CardAlignable: React.FC<CardAlignableProps> = ({
  icon,
  iconSize = "small",
  title,
  text,
  links,
  backgroundColor = "primary",
  aligning = "left",
  verticalPadding = "default",
  horizontalPadding = "default",
  roundedCorners = "responsive",
  cardAspectRatio,
  className,
  onCardClick,
  onCardHover,
  onLinkClick,
  ...props
}) => {
  const hasLink = Boolean(links && links.length > 0);

  const handleLinkClick = (linkText: string, linkIndex: number) => {
    if (onLinkClick) {
      onLinkClick(linkText, linkIndex);
    } else if (onCardClick) {
      onCardClick(undefined, undefined, linkText);
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const isLinkClick = target.closest("a");

    if (onCardClick && !isLinkClick) {
      onCardClick(undefined, undefined, undefined);
    }
  };

  return (
    <div
      className={cn(
        cardAlignable({
          backgroundColor,
          hasLink,
          aligning,
          verticalPadding,
          horizontalPadding,
          roundedCorners,
        }),
        className,
      )}
      onClick={handleCardClick}
      onMouseEnter={onCardHover}
      style={{ cursor: hasLink ? "pointer" : undefined }}
      {...props}
    >
      {/* First Section - Content */}
      <div className="uipm:flex uipm:flex-col uipm:justify-between uipm:h-full uipm:gap-[var(--container-gutter)]">
        {/* Icon/Image */}
        {icon && (
          <div className={iconContainer({ iconSize, aligning })}>
            {iconSize === "image" ? (
              <ImageWithAspectRatio
                {...icon}
                aspectRatio={cardAspectRatio || icon.aspectRatio}
              />
            ) : (
              <Image
                src={icon.src}
                alt={icon.alt}
                width={iconSize === "large" ? 80 : 64}
                height={iconSize === "large" ? 80 : 64}
                className="uipm:object-contain uipm:aspect-square"
              />
            )}
          </div>
        )}
        <div
          className={
            "uipm:flex uipm:flex-col uipm:gap-[var(--container-gutter)]"
          }
        >
          {/* Title */}
          {title && <Title {...title} className={title.className} />}

          {/* Text */}
          {text && (
            <div className={textWrapper({ backgroundColor })}>
              <SimpleRichText {...text} />
            </div>
          )}
        </div>
      </div>

      {/* Second Section - Links */}
      {!!links?.length && (
        <div className="uipm:flex uipm:flex-col uipm:gap-2">
          {links.map((linkItem, index) => (
            <Link
              key={index}
              href={linkItem.href}
              target={linkItem.target}
              noFollow={linkItem.noFollow}
              className={linkWrapper({ backgroundColor })}
              onClick={() => handleLinkClick(linkItem.text, index)}
            >
              <span>{linkItem.text}</span>
              <ChevronIcon className={linkWrapper({ backgroundColor })} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
